import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import FruitifyLogo from "../../../shared-components/FruitifyLogo";
import {
  RegistrationCard,
  FruitifyStack,
  FruitifyContainer,
} from "../../../shared-components/Containers";
import { Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { enqueueSnackbar } from "notistack";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import { useTrackingAnalytics } from "../../../hooks/useTrackingAnalytics";
import Autocomplete from "@mui/material/Autocomplete";
import { useAuthenticationStore } from "../../../stores/auth";
import {
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";

const validateName = (name, fieldName) => {
  if (!name) {
    return `${fieldName} must be provided`;
  } else if (!/^[A-Za-z]+$/.test(name)) {
    return `${fieldName} can only contain letters`;
  } else {
    return "";
  }
};

const DemoRedirectDialog = ({ open, onClose, redirectUrl }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Thanks for registering 🎉</DialogTitle>

      <DialogContent>
        <p>
          Your demo is ready. You can open it using the button below or use the
          link.
        </p>

        <Link href={redirectUrl} target="_blank" rel="noopener noreferrer">
          {redirectUrl}
        </Link>

        <p style={{ marginTop: 16 }}>
          We’ve also sent this link to your email so you can come back to it
          anytime.
        </p>

        <Typography component="p" variant="caption" color="text.secondary">
          If you don’t see the email, please check your inbox, “Other” tab, or
          spam folder — sometimes school email systems filter first-time
          messages.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() =>
            window.open(redirectUrl, "_blank", "noopener,noreferrer")
          }
        >
          Open Demo →
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function DemoRegister() {
  const [errors, setErrors] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [yearGroup, setYearGroup] = useState("");
  const [subject, setSubject] = useState("");
  const { recordMarketingEvent } = useTrackingAnalytics();
  const [openRedirectDialog, setOpenRedirectDialog] = useState(false);
  const [isTeacherChecked, setIsTeacherChecked] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    school: null,
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { setUser, updateUserInfo } = useAuthenticationStore((state) => state);
  const { identifyUser } = useTrackingAnalytics();

  useEffect(() => {
    recordMarketingEvent("demo_landing", {}, true);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await apiClient.core.getAllActiveSubjects();
        setSubjects(result?.allActiveSubjects || []);
        setYearGroups(result?.allYearGroups || []);
      } catch (error) {
        console.error("Failed to fetch subjects and year groups", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const onRegister = () => {
    if (validateData()) {
      handleSubmit();
    }
  };

  const handleChange = (field, value) => {
    if (field === "school" && typeof value === "string") {
      value = value
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateData = () => {
    const newErrors = {};

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Incorrect or missing email";
    }

    const firstNameError = validateName(formData.firstName, "First name");
    if (firstNameError) {
      newErrors.firstName = firstNameError;
    }
    const surnameError = validateName(formData.lastName, "Surname");
    if (surnameError) {
      newErrors.lastName = firstNameError;
    }
    if (!formData.school) {
      newErrors.school = "School is required";
    }

    const isValid = Object.keys(newErrors).length === 0;
    setErrors(newErrors);
    return isValid;
  };

  const onSuccessfulLogin = async (data, assignment) => {
    const { token, refreshToken, refreshExpiresIn } = data;
    setUser({
      token,
      refreshToken,
      refreshExpiresIn,
    });
    const userData = await apiClient.users.me();
    identifyUser(userData.email, false);
    updateUserInfo(userData);
    setTimeout(() => {
      navigate(`/assignments/view/${assignment.id}`, { replace: true });
    }, 100);
  };

  const handleLogin = async (registerResponse) => {
    const { tempPassword, assignment } = registerResponse;
    try {
      const data = await apiClient.users.login({
        email: formData.email.toLowerCase(),
        password: tempPassword,
      });
      onSuccessfulLogin(data, assignment);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data = await apiClient.users.startDemo({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        subject: subject.id,
        yearGroup: yearGroup.id,
        school: formData.school.id,
      });
      if (data?.success) {
        if (data?.redirectUrl) {
          setRedirectUrl(data.redirectUrl);
          setOpenRedirectDialog(true);
        } else if (data?.assignment?.id) {
          enqueueSnackbar("Success. Starting Demo...", {
            variant: "success",
            autoHideDuration: 3000,
          });
          handleLogin(data);
        }
      } else {
        enqueueSnackbar(data?.message || "Something went wrong", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error registering, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  return (
    <FruitifyContainer>
      <DemoRedirectDialog
        open={openRedirectDialog}
        onClose={() => setOpenRedirectDialog(false)}
        redirectUrl={redirectUrl}
      />
      <FruitifyStack
        direction="column"
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <RegistrationCard variant="outlined">
          <Stack direction={"row"} justifyContent={"center"}>
            <FruitifyLogo />
          </Stack>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              width: "100%",
              fontSize: "clamp(2rem, 10vw, 2.15rem)",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            Fruitify Interactive Demo
          </Typography>
          <Box
            component="form"
            onSubmit={(e) => handleSubmit(e)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            {loading ? (
              <GeneralLoading />
            ) : (
              <Grid container spacing={2}>
                <Grid item size={12}>
                  <SchoolSearchAutocomplete
                    school={formData.school}
                    onChange={(_, value) => handleChange("school", value)}
                  />
                </Grid>{" "}
                <Grid item size={12}>
                  <Autocomplete
                    sx={{ flexGrow: 1 }}
                    options={subjects}
                    getOptionLabel={(option) => option.name}
                    value={subject || null} // single selection
                    onChange={(_, newValue) => setSubject(newValue)}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subjects"
                        variant="outlined"
                      />
                    )}
                    disableClearable={false} // optional: allow clearing selection
                    freeSolo={false} // prevents entering arbitrary text
                  />
                </Grid>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="year-group-label">Year Group</InputLabel>
                    <Select
                      labelId={"year-group-label"}
                      label={"Year Group"}
                      value={yearGroup}
                      onChange={(e) => setYearGroup(e.target.value)}
                      fullWidth
                    >
                      {yearGroups.map((year) => (
                        <MenuItem key={year.id} value={year}>
                          Year {year.yearGroup}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="First Name"
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    value={formData.firstName}
                    autoComplete="given-name"
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Surname"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      autoComplete="email"
                      autoFocus
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item size={12}>
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isTeacherChecked}
                          onChange={(e) =>
                            setIsTeacherChecked(e.target.checked)
                          }
                          required
                        />
                      }
                      label="I am a teacher or educator"
                    />
                  </FormControl>
                  <Typography variant={"body2"}>
                    *Fruitify does not currently offer a direct-to-student
                    product
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
          <Typography variant={"body2"}>
            By starting this demo, you agree that we may contact you with
            updates and information about Fruitify. You can opt out at any time.
          </Typography>
          <Button
            onClick={onRegister}
            color={"secondary"}
            variant="contained"
            disabled={
              !subject || !yearGroup || !formData.school || !isTeacherChecked
            }
          >
            Start
          </Button>
        </RegistrationCard>
      </FruitifyStack>
    </FruitifyContainer>
  );
}
